export type TrackPlaybackState = {
    paused: boolean
    speed: number
    startSpeed: number
    endSpeed: number
    trackPosition: number
    elapsedSeconds: number
}

type TrackPlaybackStoreDependencies = {
    hasTrackPath: () => boolean
    normalizeTrackPosition: (position: number) => number
    resolveTrackSpeed: (trackPosition: number) => number
}

export type TrackPlaybackStore = {
    getState: () => TrackPlaybackState
    subscribe: (listener: (state: TrackPlaybackState) => void) => () => void
    setPaused: (paused: boolean) => void
    togglePaused: () => boolean
    setSpeed: (speed: number) => void
    setStartSpeed: (speed: number) => void
    setEndSpeed: (speed: number) => void
    setPosition: (position: number) => void
    restart: () => void
    updatePosition: (deltaSeconds: number) => number
    sync: (
        overrides?: Partial<Pick<TrackPlaybackState, 'trackPosition' | 'speed'>>,
    ) => void
}

export function createTrackPlaybackStore(dependencies: TrackPlaybackStoreDependencies): TrackPlaybackStore {
    const playbackState: TrackPlaybackState = {
        paused: false,
        speed: 24,
        startSpeed: 8,
        endSpeed: 24,
        trackPosition: 0,
        elapsedSeconds: 0,
    }

    const playbackListeners = new Set<(state: TrackPlaybackState) => void>()

    function getState(): TrackPlaybackState {
        return { ...playbackState }
    }

    function subscribe(listener: (state: TrackPlaybackState) => void): () => void {
        playbackListeners.add(listener)
        listener(getState())
        return () => {
            playbackListeners.delete(listener)
        }
    }

    function setPaused(paused: boolean): void {
        playbackState.paused = paused
        emitPlaybackState()
    }

    function togglePaused(): boolean {
        playbackState.paused = !playbackState.paused
        emitPlaybackState()
        return playbackState.paused
    }

    function setSpeed(speed: number): void {
        playbackState.startSpeed = speed
        playbackState.endSpeed = speed
        syncPlaybackState({ speed })
    }

    function setStartSpeed(speed: number): void {
        playbackState.startSpeed = speed
        syncPlaybackState()
    }

    function setEndSpeed(speed: number): void {
        playbackState.endSpeed = speed
        syncPlaybackState()
    }

    function setPosition(position: number): void {
        syncPlaybackState({
            trackPosition: dependencies.hasTrackPath()
                ? dependencies.normalizeTrackPosition(position)
                : position,
        })
    }

    function restart(): void {
        playbackState.elapsedSeconds = 0
        syncPlaybackState({ trackPosition: 0 })
    }

    function updatePosition(deltaSeconds: number): number {
        if (!dependencies.hasTrackPath() || playbackState.paused) {
            return playbackState.trackPosition
        }
        playbackState.elapsedSeconds += deltaSeconds
        const speed = dependencies.resolveTrackSpeed(
            playbackState.trackPosition,
        )
        const nextTrackPosition = dependencies.normalizeTrackPosition(
            playbackState.trackPosition + deltaSeconds * speed,
        )
        syncPlaybackState({ trackPosition: nextTrackPosition })
        return playbackState.trackPosition
    }

    function sync(overrides: Partial<Pick<TrackPlaybackState, 'trackPosition' | 'speed'>> = {}): void {
        syncPlaybackState(overrides)
    }

    function syncPlaybackState(overrides: Partial<Pick<TrackPlaybackState, 'trackPosition' | 'speed'>> = {}): void {
        if (overrides.trackPosition !== undefined) {
            playbackState.trackPosition = overrides.trackPosition
        }
        playbackState.speed = overrides.speed ?? dependencies.resolveTrackSpeed(playbackState.trackPosition)
        emitPlaybackState()
    }

    function emitPlaybackState(): void {
        const snapshot = getState()
        playbackListeners.forEach((listener) => {
            listener(snapshot)
        })
    }

    return {
        getState,
        subscribe,
        setPaused,
        togglePaused,
        setSpeed,
        setStartSpeed,
        setEndSpeed,
        setPosition,
        restart,
        updatePosition,
        sync,
    }
}
